import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Button, Table } from "react-bootstrap";
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from "react-bootstrap";
import userService from "../../services/userService";
import RequiredFieldMessage from "../common/userMessages/requiredFieldMessage";
import TaskComplete from "../common/taskComplete";
import EducationForm from "../forms/pageForms/educationForm";
import { getEducationInputs } from "../forms/formEntities/educationEntities";
import SaveAndContinueUserMessage from "../common/userMessages/saveAndContinueMessage";

export default function Education({ user, userData, updatePage, shake }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const educationInputs = getEducationInputs(t);
  const [education, setEducation] = useState([]);
  const [noEducationInfo, setNoEducationInfo] = useState(false);
  const [showPageReqsMessage, setShowPageReqsMessage] = useState(false);

  const [modalShow, setModalShow] = useState(false);
  const onModalHide = () => {
    setModalShow(false);
    setNoEducationInfo(false);
  };

  const handleNoEducation = () => {
    setNoEducationInfo(!noEducationInfo);
    setModalShow(true);
  };

  const handleSkipStep = async () => {
    const updatedEducation = {
      isComplete: true,
      setNoEducationInfo: noEducationInfo,
      schools: [],
    };
    userData.education = updatedEducation;
    updatePage();
    navigate("../phase2-background-information");

    await userService.saveUserData(user, "education", updatedEducation);
  };

  const handlePageReqs = (data) => {
    if (data && data.length >= 1) {
      setShowPageReqsMessage(false);
      return true;
    } else {
      setShowPageReqsMessage(true);
      shake();
      return false;
    }
  };

  const handleSubmitAndContinue = async () => {
    if (handlePageReqs(education)) {
      updatePage();
      setShowPageReqsMessage(false);
      navigate("../phase2-background-information");
      await userService.saveUserData(user, "education", {
        isComplete: true,
        ...userData.education,
      });
      userData.education = {
        isComplete: true,
        ...userData.education,
      };
    } else {
      // alert("error");
    }
  };

  // Assigning input names as keys for initialValues
  const inputKeys = educationInputs.map((input) => {
    let keys = input.name;
    return keys;
  });

  // Creating initialValues by setting input keys to each have empty string value
  const initialValues = inputKeys.reduce((acc, key) => {
    acc[key] = "";
    return acc;
  }, {});

  const handleFormikSubmit = async (values, actions) => {
    Object.keys(values).forEach((key) => {
      if (values[key] === "" || !values[key]) {
        delete values[key];
      }
    });
    let newSchoolsArr = [...education, { ...values }];
    if (noEducationInfo) {
      handleSkipStep();
    } else {
      await userService.saveUserData(user, "education", {
        noEducationInfo: noEducationInfo,
        schools: newSchoolsArr,
      });
      userData.education = {
        noEducationInfo: noEducationInfo,
        schools: newSchoolsArr,
      }
      actions.resetForm(initialValues);
      setEducation(newSchoolsArr);
      handlePageReqs(newSchoolsArr);
    }
    updatePage();
  };

  const handleSchoolDelete = async (index) => {
    education.splice(index, 1);
    setEducation(education);
    updatePage();

    if (education.length >= 1) {
      await userService.saveUserData(user, "education", {
        isComplete: true,
        noEducationInfo: noEducationInfo,
        schools: education,
      });
      userData.education = {
        isComplete: true,
        noEducationInfo: noEducationInfo,
        schools: education,
      };
    } else {
      await userService.saveUserData(user, "education", {
        isComplete: false,
        noEducationInfo: noEducationInfo,
        schools: education,
      });
      userData.education = {
        isComplete: false,
        noEducationInfo: noEducationInfo,
        schools: education,
      };
    }
  };

  useEffect(() => {
    if (userData && userData.education) {
      setEducation(userData.education.schools);
    }
  }, [userData]);

  return (
    <>
      {/* {userData.education && userData.education.isComplete ? (
        <section className="main-section">
          <h2>Education</h2>
          <section className="main-section-content">
            <TaskComplete
              pageTitle="Education"
              nextLink="phase2-background-information"
            />
          </section>
        </section>
      ) : (
        <> */}
          <Modal show={modalShow} centered>
            <ModalHeader as="span" className="alert alert-info">
              <ModalTitle as="h4">{t("education.modalTitle")}</ModalTitle>
            </ModalHeader>
            <ModalBody as="section">
              <p>{t("education.modalNoSchool")}</p>
              <p>
                <Trans i18nKey="education.modalCorrect" components={{ strong: <strong /> }} />
              </p>
              <p>
                <Trans i18nKey="education.modalIncorrect" components={{ strong: <strong /> }} />
              </p>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between" as="footer">
              <SaveAndContinueUserMessage messageType="save" task="education" />
              <Button onClick={handleFormikSubmit}>{t("common.save")}</Button>
              <Button variant="outline-primary" onClick={onModalHide}>
                {t("common.cancel")}
              </Button>
            </ModalFooter>
          </Modal>
          {/* Page Instructions  */}
          <section className="main-section">
            <h2>{t("education.title")}</h2>
            <section className="main-section-content text-start">
              <p>{t("education.intro1")}</p>
              <p>{t("education.intro2")}</p>

              <div className="alert alert-info" role="alert">
                <p>{t("education.pageRequirements")}</p>
              </div>
            </section>
          </section>

          {/* Form Section  */}
          <section className="main-section">
            <h2>{t("education.formTitle")}</h2>
            <div className="main-section-content">
              {/* Form Section - Instructions  */}
              <section className="text-start">
                <p className="text-start">{t("education.formInstructions1")}</p>
                <p>{t("education.requiredFieldIndicator")}</p>
                <p className="text-start">{t("education.formInstructions2")}</p>
              </section>

              {/* Form Section - Form  */}

              <EducationForm
                shake={shake}
                education={education}
                handleNoEducation={handleNoEducation}
                handleFormikSubmit={handleFormikSubmit}
                initialValues={initialValues}
                noEducationInfo={noEducationInfo}
                userData={userData}
              />
            </div>
          </section>

          {/* Review and Submit Section  */}
          {!noEducationInfo && (
            <section className="main-section">
              <h2>{t("education.reviewTitle")}</h2>
              <section className="main-section-content">
                {/* Review and Submit Section -  Instructions  */}
                <section className="text-start">
                  <p>{t("education.reviewInstructions1")}</p>
                  <p>{t("education.reviewInstructions2")}</p>
                </section>

                {/* Review and Submit Section - Review Table  */}
                <section
                  style={{ border: "1px solid black", marginBottom: "1rem" }}
                >
                  {education.length > 0 ? (
                    <Table striped bordered hover responsive size="sm">
                      <thead>
                        <tr>
                          <th></th>
                          <th>{t("education.tableName")}</th>
                          <th>{t("education.tableCity")}</th>
                          <th>{t("education.tableDates")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {education.map((school, index) => (
                          <tr key={index}>
                            <td>
                              <Button
                                variant="danger"
                                onClick={() => handleSchoolDelete(index)}
                                size="sm"
                                disabled={userData.submissionDate}
                              >
                                {t("education.delete")}
                              </Button>
                            </td>
                            <td>{school.schoolName}</td>
                            <td>{school.schoolCity}</td>
                            <td>
                              {school.fromDate} - {school.untilDate}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p className="mb-0">{t("education.emptyReview")}</p>
                  )}
                </section>

                {/* Review and Submit Section - Submit  */}
                <section>
                  <RequiredFieldMessage
                    handleClick={() =>
                      navigate("../phase2-background-information")
                    }
                    show={showPageReqsMessage ? true : false}
                    reqMessage={t("education.minSchoolsMessage")}
                    hasButton={true}
                  />
                  <SaveAndContinueUserMessage messageType="save and continue" />
                  <Button variant="primary" onClick={handleSubmitAndContinue}>
                    {t("common.saveAndContinue")}
                  </Button>
                </section>
              </section>
            </section>
          )}
        {/* </>
      )} */}
    </>
  );
}
